<template>
  <template v-if="show">
    <Teleport :to="teleportTarget">
      <div
        v-if="showBackdrop"
        class="fixed inset-x-0 inset-y-0 opacity-60 bg-content"
        :style="{ zIndex: 30000 * zIndexLevel }"
        aria-hidden="true"
        @click.prevent="onBackdropClick"
      />
      <div
        v-bind="$attrs"
        class="vp-modal-wrapper fixed inset-x-0 inset-y-0 flex justify-center items-center"
        :class="{ 'sm:p-3': size }"
        :style="{ zIndex: 30000 * zIndexLevel + 1 }"
      >
        <div
          class="vp-modal-content flex flex-col gap-2 bg-surface rounded-sm p-4"
          :class="[sizeClass, props.class]"
        >
          <div
            v-if="showCloseButton"
            class="flex justify-end"
          >
            <VPButton
              class="translate-x-4 pr-6"
              @click.prevent="onCloseModal"
            >
              <iconify-icon
                icon="mdi:close"
                class="text-xl"
              />
            </VPButton>
          </div>
          <div
            v-if="title"
            class="flex justify-center my-2"
          >
            <h1 class="text-content text-center text-3xl">
              {{ title }}
            </h1>
          </div>
          <slot />

          <slot
            v-if="!hideActions"
            name="commands"
          >
            <div class="flex flex-row justify-center items-center gap-4">
              <VPButton
                v-if="closeLabel"
                type="button"
                class="bg-on-primary text-primary border-primary py-1 px-2 rounded-md cursor-pointer"
                @click.prevent="onCloseModal"
              >
                {{ closeLabel }}
              </VPButton>

              <VPButton
                v-if="confirmLabel"
                type="button"
                class="bg-primary text-on-primary py-1 px-2 rounded-md cursor-pointer disabled:opacity-40 disabled:pointer-events-none"
                :disabled="confirmActionDisabled"
                @click.prevent="onConfirm"
              >
                {{ confirmLabel }}
              </VPButton>
            </div>
          </slot>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import VPButton from './VPButton.vue';
import { useEscapeLayer } from '../composables/useEscapeLayer';

interface Props {
  show: boolean;
  showCloseButton?: boolean;
  showBackdrop?: boolean;
  backdropClickable?: boolean;
  title?: string;
  closeLabel?: string;
  confirmLabel?: string;
  confirmActionDisabled?: boolean;
  hideActions?: boolean;
  zIndexLevel?: number;
  size?: 'sm' | 'md' | 'lg' | 'full';
  class?: string;
  teleportTarget?: string | HTMLElement;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  show: true,
  showBackdrop: true,
  backdropClickable: true,
  zIndexLevel: 1,
  size: undefined,
  class: '',
  teleportTarget: '#vp-teleport-vp-modal',
});

// The legacy admin modal's dimensions; at `sm` its 0.75rem gutter sits inside the cap.
const SIZE_CLASSES = {
  sm: 'max-w-104 max-h-72 sm:max-w-98 sm:max-h-66',
  md: 'max-w-200 max-h-120 sm:max-w-194 sm:max-h-114',
  lg: 'max-w-280 max-h-200 sm:max-w-274 sm:max-h-194',
  full: 'max-w-screen max-h-screen',
} as const;

const sizeClass = computed(() => {
  if (!props.size) {
    return 'min-w-96';
  }

  return `w-full h-full overflow-auto ${SIZE_CLASSES[props.size]}`;
});

const emit = defineEmits<{
  (e: 'update:show', show: boolean): void;
  (e: 'backdropClick'): void;
  (e: 'close'): void;
  (e: 'confirm'): void;
}>();

const onBackdropClick = () => {
  if (props.backdropClickable) {
    emit('update:show', false);
    emit('backdropClick');
  }
};

const onCloseModal = () => {
  emit('update:show', false);
  emit('close');
};

const onConfirm = () => {
  emit('update:show', false);
  emit('confirm');
};

// `backdropClickable: false` is how a caller says "this modal is not casually dismissible"
// (VPTurnstileModal). Such a modal still claims the layer, so Escape cannot reach the
// drawer underneath it — it just declines to close on the key.
useEscapeLayer(() => {
  if (props.backdropClickable) {
    onCloseModal();
  }
}, () => props.show);
</script>
